import React from 'react';
import { debounce } from 'lodash';

import { useCurrentStateAndParams, useRouter } from '@wix/tpa-router/react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

import { GroupRole } from '@wix/ambassador-socialgroups-v3-group-member/types';

import { Search as SearchIcon } from '@wix/wix-ui-icons-common/on-stage';
import { Box } from 'wui/Box';
import { Search } from 'wui/Search';
import { CardContent } from 'wui/CardContent';
import { Dropdown, Option } from 'wui/Dropdown';
import { TextField } from 'wui/TextField';

import { useDidUpdate } from 'common/hooks';

import classes from './MembersPageFilters.scss';

export interface IFilterParams {
  nickname: string;
  role: GroupRole;
}

interface IProps {}

export function MembersPageFilters(props: IProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const { params } = useCurrentStateAndParams();

  const [role, setRole] = React.useState<GroupRole>(params.role || undefined);
  const [query, setQuery] = React.useState(params.nickname || '');

  const search = React.useCallback(
    debounce((params: IFilterParams) => {
      router.go(
        'group.members',
        { ...params, disableScrollToTop: true },
        { location: 'replace' },
      );
    }, 300),
    [],
  );

  useDidUpdate(() => {
    search({ nickname: query, role });
  }, [search, query, role]);

  if (isMobile) {
    return (
      <CardContent>
        <TextField
          wired
          value={query}
          prefix={<SearchIcon />}
          placeholder={t('groups-web.search.placeholder')}
          onChange={handleSearchChange}
        />
      </CardContent>
    );
  }

  return (
    <CardContent>
      <Box align="space-between" verticalAlign="middle">
        <Dropdown
          className={classes.filter}
          onChange={handleFilterChange}
          label={t('groups-web.members.filter') + ':'}
          value={role}
          options={[
            {
              id: undefined,
              value: t('groups-web.members.filter.all'),
              isSelectable: true,
            },
            {
              id: GroupRole.ADMIN,
              value: t('groups-web.members.filter.admins'),
              isSelectable: true,
            },
          ]}
        />

        <Search
          withClearButton
          className={classes.search}
          value={query}
          autoFocus={!!query}
          onChange={handleSearchChange}
          placeholder={t('groups-web.search.placeholder')}
          onClear={handleQueryClear}
        />
      </Box>
    </CardContent>
  );

  function handleQueryClear() {
    setQuery('');
  }

  function handleFilterChange(option: Option) {
    setRole(option.id as GroupRole);
  }

  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    setQuery(event.target.value);
  }
}

MembersPageFilters.displayName = 'MembersPageFilters';
